import dayjs from 'dayjs';
import isToday from 'dayjs/plugin/isToday';
import isYesterday from 'dayjs/plugin/isYesterday';
import utc from 'dayjs/plugin/utc';
import { capitalize } from 'lodash';
import moment from 'moment';
import { FILE_BASE_URL } from '../constants/vars';

dayjs.extend(utc);
dayjs.extend(isToday);
dayjs.extend(isYesterday);

export const renderFullName = (staff, toCapitalize = false) => {
  if (toCapitalize)
    return [
      capitalize(staff?.name),
      capitalize(staff?.fullName_Surname),
      capitalize(staff?.fullName_Name),
      capitalize(staff?.fullName_Patronymic),
    ].join(' ');
  return [staff?.fullName_Surname, staff?.fullName_Name, staff?.fullName_Patronymic].join(' ');
};

export const normalizeFIO = (fio) => {
  return fio
    .replace(/\s+/g, ' ')
    .split(' ')
    .reduce((acc, cur) => {
      if (cur === 'O‘G‘LI' || cur === 'UG‘LI' || cur === 'KIZI' || cur === 'QIZI') {
        return acc + ' ' + cur.toLowerCase();
      } else {
        return acc + ' ' + cur[0] + cur.slice(1).toLowerCase();
      }
    }, '');
};
export function formatToInitials(str) {
  const parts = str.split(/\s+/);

  if (parts.length < 3) return str;

  const [surname, name, patronymic] = parts;

  return `${capitalizeFirst(surname[0])}'. ${capitalizeFirst(name[0])}. ${
    capitalizeFirst(patronymic)[0] + capitalizeFirst(patronymic).toLowerCase().slice(1)
  }`;
}

function capitalizeFirst(word) {
  return word.charAt(0).toUpperCase() + word.slice(1);
}

export const getAbsoluteFileUrl = (url) => {
  if (!url) return '';
  const base = FILE_BASE_URL.replace(/\/+$/, '');
  const path = url.replace(/^\/+/, '');
  return `${base}/${path}`;
};

export const formatFilters = (params) => {
  return { ...params, filters: JSON.stringify(params.filters) };
};

export const filterParamsNotNull = (params) =>
  Object.fromEntries(
    Object.entries(params).filter(([_, value]) => value != null ?? value !== undefined)
  );

export const capitalizeFirstLetter = (str) => {
  if (!str) {
    return '';
  } else {
    return str.charAt(0).toUpperCase() + str.toLowerCase().slice(1);
  }
};

export const getUserDeviceInfo = (userAgent, elements) => {
  let browserList = {
      Chrome: [/Chrome\/(\S+)/],
      Firefox: [/Firefox\/(\S+)/],
      MSIE: [/MSIE (\S+);/],
      Opera: [/Opera\/.*?Version\/(\S+)/, /Opera\/(\S+)/],
      Safari: [/Version\/(\S+).*?Safari\//],
    },
    re,
    m,
    userBrowser,
    version;
  let osList = {
      Windows: [/.*(Windows).*/],
      Linux: [/Linux\/(\S+)/],
      Mac: [/Mac\/(\S+)/],
    },
    re2,
    m2,
    userOS;

  if (userAgent === undefined) userAgent = navigator.userAgent;
  if (elements === undefined) elements = 2;
  else if (elements === 0) elements = 1337;
  for (const browser in browserList) {
    while ((re = browserList[browser].shift())) {
      if ((m = userAgent.match(re))) {
        version = m[1].match(new RegExp('[^.]+(?:.[^.]+){0,' + --elements + '}'))[0];
        userBrowser = browser;
      }
    }
  }
  for (const os in osList) {
    while ((re2 = osList[os].shift())) {
      if ((m2 = userAgent.match(re2))) {
        userOS = os;
      }
    }
  }
  return `OS: ${userOS}, Browser: ${userBrowser} ${version ? 'version ' + version : ''}`;
};

export const copyTextToClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    return 'copied';
  } catch (err) {
    console.error(err);
    return err;
  }
};

export const fileSizeConverter = (bytes) => {
  const kilobytes = bytes / 1024;
  const megabytes = kilobytes / 1024;

  // If the value is less than 1 MB, return in KB
  if (megabytes < 1) {
    return `${kilobytes.toFixed(2)} KB`;
  }

  // Otherwise, return in MB
  return `${megabytes.toFixed(2)} MB`;
};

export const deepParseEscapedJson = (input) => {
  if (typeof input === 'string') {
    let processed = input;

    let prevLength = -1;
    while (prevLength !== processed.length) {
      prevLength = processed.length;
      try {
        processed = JSON.parse(processed);
        if (typeof processed === 'string') {
          continue;
        } else {
          return deepParseEscapedJson(processed);
        }
      } catch (e) {
        if (processed.includes('\\\\')) {
          processed = processed.replace(/\\\\/g, '\\');
          continue;
        }
        return processed;
      }
    }
    return processed;
  } else if (Array.isArray(input)) {
    return input.map(deepParseEscapedJson);
  } else if (input && typeof input === 'object') {
    const result = {};
    for (const key in input) {
      result[key] = deepParseEscapedJson(input[key]);
    }
    return result;
  }
  return input;
};

const pluralizeRu = (n, forms) => {
  const lastDigit = n % 10;
  const lastTwo = n % 100;
  if (lastDigit === 1 && lastTwo !== 11) return forms[0]; // singular
  if ([2, 3, 4].includes(lastDigit) && ![12, 13, 14].includes(lastTwo)) return forms[1]; // paucal
  return forms[2]; // plural
};

const translations = {
  uz: {
    hour: (h) => `${h} soat oldin`,
    yesterday: 'Kecha',
    day: (d) => `${d} kun oldin`,
    week: (w) => `${w} hafta oldin`,
    month: (m) => `${m} oy oldin`,
    year: (y) => `${y} yil oldin`,
  },
  ru: {
    hour: (h) => `${h} ${pluralizeRu(h, ['час', 'часа', 'часов'])} назад`,
    yesterday: 'Вчера',
    day: (d) => `${d} ${pluralizeRu(d, ['день', 'дня', 'дней'])} назад`,
    week: (w) => `${w} ${pluralizeRu(w, ['неделя', 'недели', 'недель'])} назад`,
    month: (m) => `${m} ${pluralizeRu(m, ['месяц', 'месяца', 'месяцев'])} назад`,
    year: (y) => `${y} ${pluralizeRu(y, ['год', 'года', 'лет'])} назад`,
  },
};

export function formatSmartTime(input, lang = 'uz') {
  const date = dayjs(input);
  const now = dayjs();
  const t = translations[lang];

  const diffMinutes = now.diff(date, 'minute');
  const diffHours = now.diff(date, 'hour');
  const diffDays = now.diff(date, 'day');
  const diffWeeks = now.diff(date, 'week');
  const diffMonths = now.diff(date, 'month');
  const diffYears = now.diff(date, 'year');

  if (diffMinutes < 1) {
    return lang === 'ru' ? 'Только что' : 'Hozirgina';
  }
  if (diffMinutes < 60) {
    return lang === 'ru'
      ? `${diffMinutes} ${pluralizeRu(diffMinutes, ['минута', 'минуты', 'минут'])} назад`
      : `${diffMinutes} daqiqa oldin`;
  }
  if (date.isToday()) {
    return t.hour(diffHours);
  }
  if (date.isYesterday()) {
    return t.yesterday;
  }

  if (diffDays < 7) {
    return t.day(diffDays);
  }

  if (diffWeeks < 4) {
    return t.week(diffWeeks);
  }

  if (diffMonths < 12) {
    return t.month(diffMonths);
  }

  return t.year(diffYears);
}

const monthNames = {
  uz: [
    'Yanvar',
    'Fevral',
    'Mart',
    'Aprel',
    'May',
    'Iyun',
    'Iyul',
    'Avgust',
    'Sentyabr',
    'Oktyabr',
    'Noyabr',
    'Dekabr',
  ],
  ru: [
    'Января',
    'Февраля',
    'Марта',
    'Апреля',
    'Мая',
    'Июня',
    'Июля',
    'Августа',
    'Сентября',
    'Октября',
    'Ноября',
    'Декабря',
  ],
};

export function formatFullDate(input, lang = 'uz') {
  const date = dayjs(input);
  const day = date.date();
  const monthName = monthNames[lang][date.month()];
  const year = date.year();
  const time = date.format('HH:mm');

  return `${day} ${monthName} ${year} ${time}`;
}

export const formatDate = (date) => {
  return moment(date).format('YYYY-MM-DDTHH:mm:ss');
};

export const getContrastColor = (hexColor) => {
  if (!hexColor) return '#000';
  const r = parseInt(hexColor.substr(1, 2), 16);
  const g = parseInt(hexColor.substr(3, 2), 16);
  const b = parseInt(hexColor.substr(5, 2), 16);

  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  return brightness > 150 ? '#000' : '#fff';
};
